import React from "react";
import { useTenant } from "../context/TenantContext";
import "../styles/privacy.css";

const Privacy = () => {
  const { tenant } = useTenant();

  // Helper function to format phone number for tel: links (same as Contact page)
  const formatTelLink = (phone) => {
    if (!phone) return "";
    return phone.replace(/[\s\-\(\)]/g, "");
  };

  // Get privacy content from tenant (loaded from backend via PrivacyContent field)
  const privacyContent = tenant?.PrivacyContent;

  // Check if we have custom content from the admin
  const hasCustomContent = privacyContent && privacyContent.trim() !== "";

  // If custom content exists, render it directly
  if (hasCustomContent) {
    return (
      <div className="privacy-page">
        <div className="container">
          <section className="privacy-hero">
            <h1>Privacy Policy</h1>
            <p>How we protect and handle your information</p>
          </section>
          <div
            className="privacy-content"
            dangerouslySetInnerHTML={{ __html: privacyContent }}
          />
          <div className="privacy-content">
            <div className="privacy-section">
              <h3>Customer support requests</h3>
              <p>
                If you contact the store through customer support, we collect
                the name, email address, subject, and message you submit (or
                the contact details on your signed-in account). We retain the
                request in the store's support ticket system so the store team
                can respond. Chat message content is encrypted at rest by the
                service, and the store may send a notification or reply using its
                configured email service.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Default content using tenant data from backend (same approach as Contact page)
  return (
    <div className="privacy-page">
      <div className="container">
        <section className="privacy-hero">
          <h1>Privacy Policy</h1>
          <p>How we protect and handle your information</p>
        </section>

        <div className="privacy-content">
          <div className="privacy-section">
            <h2>Privacy Policy</h2>
            <p>
              Your privacy is important to us at{" "}
              {tenant?.BusinessName || "our company"}. This policy explains how 
              we collect, use, and protect your personal information.
            </p>
          </div>

          <div className="privacy-section">
            <h3>Information We Collect</h3>
            <p>
              We collect information you provide directly to us, such as when 
              you create an account, make a purchase, or contact us. This may 
              include your name, email address, shipping address, phone number, 
              and payment information.
            </p>
            <p>
              If you opt in to analytics, we also record storefront pages viewed,
              a temporary visitor-session identifier, device and browser
              category, language and time zone, referring website domain, and
              campaign tags in links. The server records the connection IP
              address and browser user-agent with these events for reporting.
              These analytics details do not include your name or contact
              information unless you provide those separately.
            </p>
            <p>
              If you contact the store through customer support, we collect the
              name, email address, subject, and message you submit (or the
              contact details on your signed-in account). We retain the request
              in the store's support ticket system so the store team can respond,
              and send a notification or reply using the store's configured
              email service.
            </p>
          </div>

          <div className="privacy-section">
            <h3>How We Use Your Information</h3>
            <ul>
              <li>Process your orders and payments</li>
              <li>Communicate with you about your orders</li>
              <li>Send you promotional offers (with your consent)</li>
              <li>Improve our services and customer experience</li>
              <li>Prevent fraud and ensure security</li>
            </ul>
          </div>

          <div className="privacy-section">
            <h3>Information Sharing</h3>
            <p>
              We do not sell, trade, or rent your personal information to third 
              parties. We may share information with service providers who assist 
              us in operating our website, conducting our business, or servicing you.
            </p>
          </div>

          <div className="privacy-section">
            <h3>Data Security</h3>
            <p>
              We implement industry-standard security measures to protect your 
              personal information. All transactions are encrypted using SSL technology.
            </p>
          </div>

          <div className="privacy-section">
            <h3>Cookies</h3>
            <p>
              We use necessary storage for core site functions. Storefront
              analytics are recorded only when you opt in to Analytics in the
              cookie preferences. You can change your preferences using the
              cookie controls or adjust your browser settings.
            </p>
          </div>

          <div className="privacy-section">
            <h3>Contact Us</h3>
            <p>If you have questions about this privacy policy, please contact us:</p>
            
            {/* Email - from backend */}
            {tenant?.BusinessEmail && (
              <p>
                <strong>Email:</strong>{" "}
                <a href={`mailto:${tenant.BusinessEmail}`}>
                  {tenant.BusinessEmail}
                </a>
              </p>
            )}
            
            {/* Phone - from backend with proper formatting */}
            {tenant?.BusinessPhone && (
              <p>
                <strong>Phone:</strong>{" "}
                <a href={`tel:${formatTelLink(tenant.BusinessPhone)}`}>
                  {tenant.BusinessPhone}
                </a>
              </p>
            )}
            
            {/* Address - from backend */}
            {(tenant?.AddressLine1 || tenant?.City) && (
              <p>
                <strong>Address:</strong>{" "}
                {[
                  tenant?.AddressLine1,
                  tenant?.AddressLine2,
                  tenant?.City,
                  tenant?.State,
                  tenant?.PostalCode,
                  tenant?.Country
                ].filter(Boolean).join(", ")}
              </p>
            )}
            
            {/* Fallback contact info if no tenant data exists */}
            {!tenant?.BusinessEmail && 
             !tenant?.BusinessPhone && 
             !tenant?.AddressLine1 && 
             !tenant?.City && (
              <p>
                <strong>Email:</strong>{" "}
                <a href="mailto:privacy@example.com">privacy@example.com</a>
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Privacy;